import { VueAppIcon } from '@/components/brand/vue/vue-app-icon'
import { type Tone } from '@/lib/tone'

import { type ComponentType } from 'react'

import {
  ShieldUser,
  ChartArea,
  Truck,
  HardHat,
  FolderKanban,
  HandCoins,
  Building2,
  HeartHandshake,
  Landmark,
  PackageSearch,
  CircleUser,
  DraftingCompass,
  Headset,
  Gauge,
  LibraryBig
} from 'lucide-react'

/**
 * One entry in the app registry
 */
export interface ApplicationConfigEntry {
  key: string
  name: string
  icon: ComponentType<{ className?: string }>
  baseUrl: string
  category?: string
  opensInNewTab?: boolean
  ai?: boolean
  /**
   * Serves an audience outside WorldVue. Internal-only affordances stay off these surfaces even for staff who can
   * open them — Vue is the first.
   */
  external?: boolean
}

const applicationsConfig = {
  admin: {
    key: 'admin',
    name: 'Admin',
    icon: ShieldUser as ComponentType<{ className?: string }>,
    baseUrl: '/admin'
  },
  internal: {
    key: 'internal',
    name: 'Internal Portal',
    icon: ChartArea as ComponentType<{ className?: string }>,
    baseUrl: '/internal/dashboard'
  },
  dispatching: {
    // Display renamed from "Dispatching" 2026-07 as the app grew beyond
    // dispatch into field-tech management (estimates, invoices, AP). The key
    // and slug deliberately stay `dispatching` — they're welded into prod
    // permission grants and analytics history (app_area) and must not change
    // meaning mid-history.
    key: 'dispatching',
    name: 'Field Operations',
    icon: Truck as ComponentType<{ className?: string }>,
    baseUrl: '/dispatching/dashboard'
  },
  technician: {
    // The external app for 3rd-party field-technician orgs. Renamed from
    // "Field Operations" (key `fieldops`) 2026-07 while it had zero external
    // consumers, freeing the Field Operations name for the internal app.
    key: 'technician',
    name: 'Technician Portal',
    icon: HardHat as ComponentType<{ className?: string }>,
    baseUrl: '/technician/dashboard',
    external: true
  },
  deployments: {
    key: 'deployments',
    name: 'Deployments',
    icon: FolderKanban as ComponentType<{ className?: string }>,
    baseUrl: '/deployments/dashboard'
  },
  sales: {
    key: 'sales',
    name: 'Sales',
    icon: HandCoins as ComponentType<{ className?: string }>,
    baseUrl: '/sales/dashboard'
  },
  accounting: {
    // AP-facing app. v1 is the S2P technician-invoice release queue;
    // BC-sourced Purchase Invoices and Sales Invoices surfaces are the
    // planned siblings, hence a per-audience base rather than a generic
    // /invoices.
    key: 'accounting',
    name: 'Accounting',
    icon: Landmark as ComponentType<{ className?: string }>,
    baseUrl: '/accounting/dashboard'
  },
  logistics: {
    key: 'logistics',
    name: 'Logistics',
    icon: PackageSearch as ComponentType<{ className?: string }>,
    baseUrl: '/logistics/board'
  },
  'service-center': {
    key: 'service-center',
    name: 'Service Center',
    icon: Headset,
    baseUrl: '/service-center'
  },
  design: {
    // Sales-to-Design request queue (epic wci/central&58). Its own app rather
    // than a Sales section: Designers and Design Managers work here without
    // the Sales app, and Sales and Deployments show the same tasks read-only.
    key: 'design',
    name: 'Design',
    icon: DraftingCompass as ComponentType<{ className?: string }>,
    baseUrl: '/design/queue'
  },
  metrics: {
    // The governed metric dictionary + explorer for leaders. Its own app
    // rather than an Internal section, so access is granted separately: a
    // leader can have Metrics without the Internal Portal.
    key: 'metrics',
    name: 'Metrics',
    icon: Gauge as ComponentType<{ className?: string }>,
    baseUrl: '/metrics/dictionary'
  },
  knowledge: {
    // The company knowledge base (formerly kb.worldvue.com), written in the
    // app. Its own app so feature how-tos ship in the same MR as the feature,
    // and so access is granted separately from Internal.
    key: 'knowledge',
    name: 'Knowledge',
    icon: LibraryBig as ComponentType<{ className?: string }>,
    baseUrl: '/knowledge'
  },
  portal: {
    key: 'portal',
    name: 'Portal',
    icon: Building2 as ComponentType<{ className?: string }>,
    baseUrl: '/portal',
    external: true
  },
  'client-success': {
    key: 'client-success',
    name: 'Client Success',
    icon: HeartHandshake as ComponentType<{ className?: string }>,
    baseUrl: '/client-success'
  },
  me: {
    // The cross-app personal area: Inbox + Profile (and the preference,
    // notification, and alert-subscription tabs that hang off Profile).
    // Everyone has one, so it is UNGATED — there is no `me:read` permission
    // and `useSelectApp` special-cases it into the switcher for every user.
    // It is also NOT selectable as a default app: `me` is deliberately absent
    // from /api/preferences' VALID_APP_KEYS and from APP_PRIORITY, and the
    // Default Application preference filters out apps that can't be a landing
    // default. A personal area is not a work surface to land on.
    key: 'me',
    name: 'My Atlas',
    icon: CircleUser as ComponentType<{ className?: string }>,
    baseUrl: '/me/inbox'
  },
  vue: {
    // The standalone AI workspace
    key: 'vue',
    name: 'Vue',
    // The brand mark, not a generic sparkle: this is Vue, not "some AI".
    icon: VueAppIcon,
    baseUrl: '/vue',
    opensInNewTab: true,
    ai: true
  }
} satisfies Record<string, ApplicationConfigEntry>

// Widened to `ApplicationConfigEntry` values while `keyof` stays the precise key union
export const applications: Record<
  keyof typeof applicationsConfig,
  ApplicationConfigEntry
> = applicationsConfig

/** An application's key — the single source of truth for the app registry. */
export type AppKey = keyof typeof applications

/**
 * App-key → tone slug. Each value resolves to a `.tone-{slug}-*` family of
 * utility classes defined in globals.css (tile, icon, active-bg, accent-bar,
 * label, dot). Centralizing the tone choice here means swapping an app's
 * brand color is a one-line change.
 */
export const appToneMap: Record<keyof typeof applications, Tone> = {
  admin: 'red',
  internal: 'blue',
  dispatching: 'orange',
  technician: 'amber',
  deployments: 'green',
  sales: 'emerald',
  accounting: 'violet',
  logistics: 'cyan',
  design: 'rose',
  metrics: 'lime',
  knowledge: 'yellow',
  'service-center': 'teal',
  portal: 'purple',
  'client-success': 'teal',
  // Distinct from every work app AND from APP_TONE_FALLBACK ('zinc') — a zinc
  // app would be indistinguishable from one with no tone mapping at all.
  me: 'indigo',
  vue: 'violet'
}

/** Fallback tone slug when an app key isn't in `appToneMap`. */
export const APP_TONE_FALLBACK: Tone = 'zinc'

/**
 * The accent for the landing-app affordances — the badge, the footer strip, and
 * the pick-mode bar and tile hover.
 */
export const LANDING_TONE: Tone = 'emerald'

/**
 * Nav-scope → tone. The portal sidebar splits navigation into scope blocks
 * (Organization / Property / System); each renders in a fixed tone. These are
 * deliberately separate from `appToneMap` — a scope's color is about *where you
 * are in the hierarchy*, not which app you're in.
 *
 * Shared by the sidebar (`nav-main`) and `useSectionTone()` so the scope-block
 * tile and the section headings on a page can never disagree about a scope's
 * color. Change a scope's color once here.
 */
export const SCOPE_TONES = {
  organization: 'blue',
  property: 'emerald',
  system: 'amber'
} as const satisfies Record<string, Tone>

export type Scope = keyof typeof SCOPE_TONES
